import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/uk";
import { useState } from "react";
import { ISettings } from "../../../models/Settings";
import useAppSelector from "../../../hooks/useAppSelector";
import { useTranslation } from "react-i18next";
import { QUERY_CONSTANTS } from "../../../api/queryKeys";
import { useClassEventReservationMutations, useOpenGymMutations } from "../../../api/mutations";
import { handleError } from "../../../helper/errorHandler";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { useGetOpenGymsByDateAndGymIdQuery } from "../../../api/queries";
import { IOpenGym } from "../../../models/OpenGym";

interface IProps {
  settings: ISettings | null;
  gymId: string;
  today: string;
}

export const useOpenGymsTab = ({ settings, gymId, today }: IProps) => {
  const { user } = useAppSelector((state) => state.userProfileSlice);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const [selected, setSelected] = useState<IOpenGym | null>(null);
  const [isConfirmVisible, setConfirmVisible] = useState(false);
  const [isSuccessVisible, setSuccessVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(today);
  const [isUsingLoyalty, setIsUsingLoyalty] = useState<boolean>(false);

  const todayStart = dayjs().startOf("day");
  const visibilityWeeks = settings?.classVisibilityWeeks ?? 2;
  const maxDate = todayStart.add(visibilityWeeks, "weeks").endOf("day");

  const { createOpenGymReservation } = useOpenGymMutations();

  const {
    data: classes = [],
    refetch,
    isLoading,
  } = useGetOpenGymsByDateAndGymIdQuery({
    gymId,
    date: selectedDate,
    userId: user?.id || "",
  });

  const onSelect = (item: IOpenGym) => {
    setSelected(item);
    setConfirmVisible(true);
    setIsUsingLoyalty(false);
  };

  const onCloseConfirm = () => {
    setConfirmVisible(false);
    setSelected(null);
    setIsUsingLoyalty(false);
  };

  const onConfirmBooking = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);

    createOpenGymReservation(
      {
        gymListingId: gymId!,
        classEventId: selected.classEventId,
        startDate: selected.occuranceDate,
        endDate: selected.endDate,
        useLoyaltyPoints: isUsingLoyalty,
      },
      {
        onSuccess: () => {
          setSubmitting(false);
          setConfirmVisible(false);
          setSuccessVisible(true);
          setIsUsingLoyalty(false);
          refetch();

          queryClient.invalidateQueries({
            queryKey: [QUERY_CONSTANTS.GET_UPCOMING_CLASS_EVENTS_RESERVATIONS],
          });
        },
        onError: (e) => {
          setSubmitting(false);
          setConfirmVisible(false);
          setIsUsingLoyalty(false);
          handleError(e, dispatch, t);
          refetch();
        },
      }
    );
  };

  const onCloseSuccess = () => {
    setSuccessVisible(false);
  };

  const changeDate = (direction: "next" | "prev") => {
    const current = dayjs(selectedDate);
    if (direction === "prev") {
      if (current.isSame(todayStart, "day")) return;
      setSelectedDate(current.subtract(1, "day").format("YYYY-MM-DD"));
    } else {
      const nextDate = current.add(1, "day");
      if (nextDate.isAfter(maxDate, "day")) return;
      setSelectedDate(nextDate.format("YYYY-MM-DD"));
    }
  };

  const isPrevDisabled = dayjs(selectedDate).isSame(today, "day");
  const isNextDisabled =
    dayjs(selectedDate).isAfter(maxDate, "day") || dayjs(selectedDate).isSame(maxDate, "day");

  return {
    t,
    classes,
    isLoading,
    selectedDate,
    selected,
    submitting,
    isConfirmVisible,
    isSuccessVisible,
    onSelect,
    onCloseConfirm,
    onConfirmBooking,
    onCloseSuccess,
    changeDate,
    isPrevDisabled,
    isNextDisabled,
    setIsUsingLoyalty,
  };
};
